import React from 'react';
import {Button, Typography} from '@material-ui/core';
// Font Awesome icons for your 5-button layout:
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faUndo,
  faRotateRight,
  faTrash,
  faRotateLeft,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import Add from '@material-ui/icons/Add';

/**
 * We define the drawingMode as a string union:
 * 'POLYGON' or null, so we don't need 'google.maps.drawing.OverlayType.POLYGON'.
 */
type DrawingModeType = 'POLYGON' | null;

interface AddDataButtonsProps {
  drawingMode: DrawingModeType;
  setDrawingMode: React.Dispatch<React.SetStateAction<DrawingModeType>>;

  // Additional props for the advanced flow
  polygonIsComplete?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onDelete?: () => void;
  onReset?: () => void;
  onSave?: () => void;
  /** Opens the "Add New Data Point" form (field must be selected) */
  onAddData?: () => void;
}

const AddDataButtons: React.FC<AddDataButtonsProps> = ({
  drawingMode,
  setDrawingMode,
  polygonIsComplete,
  onUndo,
  onRedo,
  onDelete,
  onReset,
  onSave,
  onAddData,
}) => {
  const isDrawing = drawingMode === 'POLYGON';

  if (!isDrawing) {
    // ========== IDLE STATE: the original 2-button layout ==========
    return (
      <div className="bottom-middle-box">
        {/* "Add data" button */}
        <Button
          style={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            paddingInline: 10,
            borderRadius: 10,
            background: '#ACECCE',
            marginRight: 8,
          }}
          onClick={onAddData}
        >
          <Add />
          <Typography style={{marginLeft: 4}}>Add data</Typography>
        </Button>

        {/* "Add field" to enter polygon drawing mode */}
        <Button
          style={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            paddingInline: 10,
            borderRadius: 10,
            background: '#E7E5E4',
          }}
          onClick={() => setDrawingMode('POLYGON')}
        >
          <Add />
          <Typography style={{marginLeft: 4}}>Add field</Typography>
        </Button>
      </div>
    );
  } else {
    // ========== DRAWING STATE: show 5-button layout (undo, redo, delete, cancel, save) ==========
    return (
      <div className="bottom-middle-box" style={{display: 'flex', gap: 8}}>
        {/* Undo */}
        <Button
          style={{
            borderRadius: 12,
            background: '#FFFFFF',
          }}
          onClick={onUndo}
        >
          <FontAwesomeIcon icon={faUndo} style={{color: '#013333'}} />
        </Button>

        {/* Redo */}
        <Button
          style={{
            borderRadius: 12,
            background: '#FFFFFF',
          }}
          onClick={onRedo}
        >
          <FontAwesomeIcon icon={faRotateRight} style={{color: '#013333'}} />
        </Button>

        {/* Delete entire polygon */}
        <Button
          style={{
            borderRadius: 12,
            background: '#FFFFFF',
          }}
          onClick={onDelete}
        >
          <FontAwesomeIcon icon={faTrash} style={{color: '#013333'}} />
        </Button>

        {/* "Cancel polygon" => calls onReset, setDrawingMode(null) */}
        <Button
          style={{
            borderRadius: 12,
            background: '#FFFFFF',
            color: '#013333',
            fontWeight: 600,
            minWidth: 120,
            textTransform: 'none',
          }}
          onClick={() => {
            onReset?.();
            setDrawingMode(null);
          }}
        >
          <FontAwesomeIcon
            icon={faRotateLeft}
            style={{color: '#013333', marginRight: 6}}
          />
          Cancel polygon
        </Button>

        {/* "Save changes": disabled if polygonIsComplete === false */}
        <Button
          style={{
            borderRadius: 12,
            background: '#ACECCE',
            color: '#013333',
            fontWeight: 600,
            minWidth: 120,
            textTransform: 'none',
          }}
          disabled={!polygonIsComplete}
          onClick={() => {
            onSave?.();
            setDrawingMode(null);
          }}
        >
          <FontAwesomeIcon
            icon={faCheck}
            style={{color: '#013333', marginRight: 6}}
          />
          Save changes
        </Button>
      </div>
    );
  }
};

export default AddDataButtons;
